import {
  UserSettingObject,
  TabType,
  ValuationPeriodState,
  VsPeerState,
  ValuationGridData,
} from "../valuation-page/valuation-page.model";

import {
  CY_PERIOD_BASIS,
  FY_PERIOD_BASIS,
  ABS_PERIOD_TYPE,
  REL_PERIOD_TYPE,
  ABS_MIN_YEAR_RANGE,
  ABS_MAX_YEAR_RANGE,
  REL_BASE_YEAR,
  DEFAULT_TO_YEAR,
  DEFAULT_FROM_YEAR,
  REL_MIN_YEAR_RANGE,
  REL_MAX_YEAR_RANGE,
  ACTUAL_CALENDAR_YEAR,
} from "../valuation-page/valuation-page.constant";

import { Dictionary } from "../store/models/common_types";

import { convertPeriodMetaToUserSettings } from "@va/core-library/period-filter";
import { convertRelativePeriodLabelToId } from "src/app/utils/period-utils";
import { formatMCapAndEVCell } from "src/app/utils/common";

/**
 * This method gives the array of usliid of selected ratios
 * @param {array} ratios - ratio_details array that we get from meta data
 * @returns arr of selected usliid
 */
export function getSelectedRatiosId(ratios) {
  return ratios.reduce(
    (previousValue, currentValue) =>
      currentValue.ratio.selected
        ? previousValue.concat(currentValue.ratio.usliid)
        : previousValue,
    [],
  );
}

/**
 * This method gives the array of names of selected items
 * @param {array} arr - array of element which has selected and name property
 * @returns arr of selected names
 */
export function getNameArrAccordingToSelectedState(
  arr: Array<{ selected: boolean; name: string; [x: string]: any }>,
) {
  return arr.reduce(
    (previousValue, currentValue) =>
      currentValue.selected
        ? previousValue.concat(currentValue.name)
        : previousValue,
    [],
  );
}

/**
 * Determines if the donom is selected or not
 * @param {array} ratios - ratio.include array of state
 * @returns true/false implying denom is selected or not
 */
export function getIsDenomSelected(ratios) {
  return (
    ratios && (ratios.find((item) => item.name === "denom") || {}).selected
  );
}

/**
 * Determines if the denom_yoy is selected or not
 * @param {array} ratios - ratio.include array of state
 * @returns true/false implying denom_yoy is selected or not
 */
export function getIsDenomYoySelected(arr) {
  return arr && (arr.find((item) => item.name === "denom_yoy") || {}).selected;
}

/**
 * Determines if the selected period type is absolute or not
 * @param {array} periodType - periods.type array that we get from meta data
 * @returns true/false implying period type ais absolute or not
 */
export function getIsAbsolutePeriod(periodType) {
  return (periodType.find((item) => item.name === ABS_PERIOD_TYPE.id) || {})
    .selected;
}

/**
 * Determines if the selected period basis is calendar or not
 * @param {array} periodBasis - periods.basis array that we get from meta data
 * @returns true/false implying period basis ais cal or not
 */
export function getIsCalYearSelected(periodBasis) {
  return (periodBasis.find((item) => item.name === CY_PERIOD_BASIS.id) || {})
    .selected;
}

/**
 * It is used to get the relative value from absolute year
 * @param {number} year - Absolute year in number
 * @param {number} zeroBaseAbsYear - Base year, absolute year of cy0/fy0
 * @returns {number} relative year value
 */
export function getRelativeYearFromAbs(year: number, zeroBaseAbsYear) {
  const diff = Math.abs(zeroBaseAbsYear - year);
  return year < zeroBaseAbsYear ? diff * -1 : diff;
}

/**
 * It is used to get the absolute value from relative value of year
 * @param {number} relativeYear - relative year in number
 * @param {number} zeroBaseAbsYear - Base year, absolute year of cy0/fy0
 * @returns {number} absolute year value
 */
export function getAbsYear(relativeYear: number, zeroBaseAbsYear) {
  return zeroBaseAbsYear + relativeYear;
}

/**
 * Determines relative value of given period
 * @param {number} periods - period value
 * @returns {number} relative value of given period
 */
export function getRelativePeriod(period, isAbsType, zeroBaseAbsYear): number {
  return isAbsType ? getRelativeYearFromAbs(period, zeroBaseAbsYear) : period;
}

/**
 * Determines selected period type
 * @param {array} periodType - periods.type array that we get from meta data
 * @returns {string} selected period type [abs | rel]
 */
export function getSelectedPeriodType(periodType): string {
  const isAbsolutePeriod = getIsAbsolutePeriod(periodType);
  return isAbsolutePeriod ? ABS_PERIOD_TYPE.id : REL_PERIOD_TYPE.id;
}

/**
 * Determines selected period basis
 * @param {array} periodBasis - periods.basis array that we get from meta data
 * @returns {string} selected period type [cal | fy]
 */
export function getSelectedPeriodBasis(periodBasis): string {
  const isCalYear = getIsCalYearSelected(periodBasis);
  return isCalYear ? CY_PERIOD_BASIS.id : FY_PERIOD_BASIS.id;
}

/**
 * This method id to get the array of company id from tickers
 * @param {array} tickerInfo - ticker_info array that we get from meta data
 * @returns {array} array of company id
 */
export function getTickersCompanyId(tickerInfo) {
  return tickerInfo.map((item) => item.companyid || item.cid);
}

/**
 * This method id to get the array of ticker id from tickers
 * @param {array} tickerInfo - ticker_info array that we get from meta data
 * @returns {array} array of ticker id
 */
export function getTickersId(arr = []) {
  return arr.map((item) => item.ticker);
}

/**
 * This method is use to set the userSetting details, which we can use for creating request data for userSetting api
 * @param {string} tabType - type of tab [peer | company]
 * @param {string} currentTicker - Id of main ticker
 * @param {object} ratios - ratio object state
 * @param {object} periods - period object state
 * @param {string} currency - selected currency
 *  @param {array} tickers - ticker_info array that we get from meta data
 * @returns {UserSettingObject} user setting details
 */
export function getUserSettingDetails(obj: {
  tabType;
  currentTicker;
  ratios;
  periods;
  tickers: Array<any>;
  currency?;
  revision?;
  scaleType?;
  orientation?;
}): UserSettingObject {
  const selectedRatios = getSelectedRatiosId(obj.ratios.ratio_details);
  const periodUserSettings = convertPeriodMetaToUserSettings(obj.periods);
  const userSettingDetails: UserSettingObject = {
    tabType: obj.tabType,
    currentTicker: obj.currentTicker,
    selectedRatios: selectedRatios,
    isDenomSelected: getIsDenomSelected(obj.ratios.include),
    isDenomYoySelected: getIsDenomYoySelected(obj.ratios.include),
    currency: obj.currency,
    tickers: getTickersCompanyId(obj.tickers),
    orientation: obj.orientation,
    revision: obj.revision,
    scaleType: obj.scaleType,
    ...periodUserSettings,
  };
  return userSettingDetails;
}

/**
 * This method is use to update the already created userSetting details.
 * We can pass the property and their value that we want to update
 * @param {UserSettingObject} userSettingDetails - userSetting object which we needs to update
 * @param {object} objToUpdate - object with property and their value, which we want to update
 * @returns {UserSettingObject} updated user setting details
 */
export function updateUserSettingDetails(
  userSettingDetails: UserSettingObject,
  objToUpdate: {
    tabType?: TabType;
    currentTicker?: string;
    selectedRatios?: Array<number>;
    isDenomSelected?: boolean;
    isDenomYoySelected?: boolean;
    currency?: string;
    tickers?: Array<number>;
    revision?: string;
    scaleType?: number;
    orientation?: string;
    type?: string;
    basis?: string;
    from?: number;
    to?: number;
    other?: Array<string>;
    period_type_basis_from?: string;
    period_type_basis_to?: string;
  },
): UserSettingObject {
  const updatedUserSettingDetails = {
    ...userSettingDetails,
    ...objToUpdate,
  };
  return updatedUserSettingDetails;
}

/**
 * It is used to get the prefix according selected period basis
 * @param {boolean} isCyYear indicates if selected year basis is Calendar or not
 * @returns {string} period basis prefix
 */
export function getBasisPrefix(isCalYear) {
  return isCalYear ? CY_PERIOD_BASIS.prefix : FY_PERIOD_BASIS.prefix;
}

/**
 * It is used to get the prefix used for relative periods
 * @param {boolean} isCyYear indicates if selected year basis is Calendar or not
 * @returns {string} relative period basis prefix
 */
export function getRelativeBasisPrefix(isCalYear) {
  return isCalYear
    ? CY_PERIOD_BASIS.relativePrefix
    : FY_PERIOD_BASIS.relativePrefix;
}

/**
 * It is used to create year label value for period filter
 * @param {number} year selected from year value
 * @param {boolean} isCyYear indicates if selected year basis is Calendar or not
 * @param {boolean} isAbsType indicates if selected year type is Absolute or not
 * @returns {string} formatted string of year, it adds prefix according to selected period basis
 */
export function getFormattedYear(year: number, isCyYear, isAbsType) {
  const prefix = getBasisPrefix(isCyYear);
  const prefixToAdd = isAbsType ? `${prefix}-` : prefix;
  return `${prefixToAdd}${year}`;
}

/**
 * It is used to create label value for period filter
 * @param {boolean} isAbsType indicates if selected year type is Absolute or not
 * @param {boolean} isCyYear indicates if selected year basis is Calendar or not
 * @param {number} selectedFrom selected from year value
 * @param {number} selectedTo selected to year value
 * @param {array} otherValues other period data
 * @returns {string} formatted string of selected filters
 */
export function createSelectedPeriodLabel(
  isAbsType,
  isCyYear,
  selectedFrom,
  selectedTo,
  otherValues,
) {
  let result = "";
  result += `${getFormattedYear(
    selectedFrom,
    isCyYear,
    isAbsType,
  )} - ${getFormattedYear(selectedTo, isCyYear, isAbsType)}`;
  let otherValuesStr = "";
  for (const item of otherValues) {
    if (item.selected) {
      otherValuesStr += `, ${item.label}`;
    }
  }
  result += otherValuesStr;
  return result;
}

/**
 * It is used to get the default absolute values of year.
 * @returns {object} min, max, fromValue, toValue of year
 */
export function getDefaultMinMaxSelectedValues() {
  const minValue = ABS_MIN_YEAR_RANGE;
  const maxValue = ABS_MAX_YEAR_RANGE;
  const selectedFromValue = DEFAULT_FROM_YEAR;
  const selectedToValue = DEFAULT_TO_YEAR;
  return {
    min: minValue,
    max: maxValue,
    fromValue: selectedFromValue,
    toValue: selectedToValue,
  };
}

/**
 * It is used to get min and max value of year
 * It formats it according to selected period type
 * @param {boolean} isAbsType indicates if selected year type is Absolute or not
 * @returns {object} min, max value of year for Number Input
 */
export function getMinMAxYearRange(isAbsType) {
  const relativeRange = {
    min: REL_MIN_YEAR_RANGE,
    max: REL_MAX_YEAR_RANGE,
  };
  const absoluteRange = {
    min: ABS_MIN_YEAR_RANGE,
    max: ABS_MAX_YEAR_RANGE,
  };
  return isAbsType ? absoluteRange : relativeRange;
}

/**
 * It is used to convert the absolute value of year to relative values and vice versa.
 * It converts it according to selected period type
 * @param {number} fromYear period state
 * @param {number} toYear period state
 * @param {string} selectedType ('abs'|'rel')
 * @returns {object} converted values of min, max, fromValue, toValue for Number Input
 */
export function convertMinMaxFromToPeriodAccordingPeriodType(
  fromYear,
  toYear,
  selectedType,
  isCyYear,
  zeroBaseFiscalAbsYear,
) {
  let { min, max, fromValue, toValue } = getDefaultMinMaxSelectedValues();
  const zeroBaseAbsYear = !isCyYear
    ? zeroBaseFiscalAbsYear
    : ACTUAL_CALENDAR_YEAR;
  if (selectedType === REL_PERIOD_TYPE.id) {
    min = REL_MIN_YEAR_RANGE;
    max = REL_MAX_YEAR_RANGE;
    fromValue = getRelativeYearFromAbs(fromYear, zeroBaseAbsYear);
    toValue = getRelativeYearFromAbs(toYear, zeroBaseAbsYear);
  } else {
    fromValue = getAbsYear(fromYear, zeroBaseAbsYear);
    toValue = getAbsYear(toYear, zeroBaseAbsYear);
  }

  return {
    min,
    max,
    fromValue,
    toValue,
  };
}

/**
 * It is used to retrieve the label of selected year values
 * It creates the year label from range of selected year and formats it according to period basis and type
 * @param {object} periodData period state
 * @returns {array} array of formatted year
 */
export function getFormattedSelectedYearColArray(periodData) {
  const result = [];
  if (periodData) {
    const fromYear = parseInt(periodData.from_to.selected_from, 10);
    const toYear = parseInt(periodData.from_to.selected_to, 10);
    const isCyYear = getIsCalYearSelected(periodData.basis);
    const isAbsType = getIsAbsolutePeriod(periodData.type);
    const prefix = getBasisPrefix(isCyYear);
    const prefixToAdd = isAbsType ? `${prefix}-` : prefix;
    for (let i = fromYear; i <= toYear; i++) {
      result.push(`${prefixToAdd}${i}`);
    }
  }
  return result;
}

/**
 * It is used to retrieve the list of selected other periods
 * @param {array} otherPeriod other period data
 * @returns {array} array of selected others period list
 */
export function getSelectedOtherPeriodListArray(otherPeriod) {
  return otherPeriod.reduce(
    (previousValue, currentValue) =>
      currentValue.selected
        ? previousValue.concat(currentValue.period_list)
        : previousValue,
    [],
  );
}

/**
 * It is used to retrieve the data id of selected period type
 * @param {string} periodType selected period type
 * @returns {string} dataId of selected period type
 */
export function getSelectedPeriodTypeDataId(periodType) {
  const isAbsolutePeriod = getIsAbsolutePeriod(periodType);
  return isAbsolutePeriod ? ABS_PERIOD_TYPE.dataId : REL_PERIOD_TYPE.dataId;
}

/**
 * It is used to get the currently selected period type
 * @returns {string} - currently selected period type
 */
export function getPeriodType(period: ValuationPeriodState): string {
  return period && period.type
    ? getSelectedPeriodTypeDataId(period.type)
    : ABS_PERIOD_TYPE.dataId;
}

/**
 * It is used to get a simple list of currently selected other periods
 * @returns {array} - currently selected other period types
 */
export function getOtherPeriods(period: ValuationPeriodState): Array<string> {
  return period && period.other_period
    ? getSelectedOtherPeriodListArray(period.other_period)
    : [];
}

/**
 * It generats a simple list of currently selected period
 * @returns {array} - currently selected periods based on period type
 */
export function getSelectedPeriods(
  period: ValuationPeriodState,
  includeOtherPeriods: boolean,
): Array<string> {
  let result = [];
  if (period) {
    const selectedYears = [];
    const otherPeriod = includeOtherPeriods ? getOtherPeriods(period) : [];
    if (period.is_annual_selected) {
      const fromYear = parseInt(period.from_to.selected_from, 10);
      const toYear = parseInt(period.from_to.selected_to, 10);
      const isCyYear = getIsCalYearSelected(period.basis);
      const isAbsType = getIsAbsolutePeriod(period.type);
      const prefix = getBasisPrefix(isCyYear);
      const prefixToAdd = isAbsType ? `${prefix}-` : prefix;
      const currentYear = isAbsType ? ACTUAL_CALENDAR_YEAR : REL_BASE_YEAR;
      let indexOfOtherPeriod = -1;
      let index = -1;
      for (let i = fromYear; i <= toYear; i++) {
        index++;
        if (i === currentYear) {
          indexOfOtherPeriod = index + 1;
        }
        selectedYears.push(`${prefixToAdd}${i}`);
      }
      if (otherPeriod && otherPeriod.length) {
        if (indexOfOtherPeriod === -1) {
          if (fromYear > currentYear) {
            result = otherPeriod.concat(selectedYears);
          } else if (toYear < currentYear) {
            result = selectedYears.concat(otherPeriod);
          }
        } else {
          result = selectedYears
            .slice(0, indexOfOtherPeriod)
            .concat(otherPeriod, selectedYears.slice(indexOfOtherPeriod));
        }
      } else {
        result = selectedYears;
      }
    } else {
      result = otherPeriod;
    }
  }

  return result;
}

export function extractTickers(
  companyids: Array<string>,
  isComparativeAnalysisCall: boolean = false,
): string {
  let tickerParams = "";
  const key = isComparativeAnalysisCall ? "ticker" : "companyid";
  for (let i = 0; i < companyids.length; i++) {
    tickerParams = `${tickerParams}&${key}=${companyids[i]}`;
  }

  return tickerParams;
}

export function extractTickerCompanyId(startIndex, batchSize, companyids) {
  const companyIdArray = [];
  for (let i = startIndex; i < startIndex + batchSize; i++) {
    if (i > companyids.length - 1) {
      break;
    } else {
      companyIdArray.push(companyids[i]);
    }
  }

  return companyIdArray;
}

export function createDataCallUrl(
  ratios: Array<string>,
  state: VsPeerState,
  optimizedTickerSet: Array<any> = [],
): Array<string> {
  const urls: Array<string> = [];

  const periodsSet = new Set<string>();
  for (let i = 0; i < ratios.length; i++) {
    const _col = ratios[i];
    if (_col.charAt(0) === "#") {
      periodsSet.add(_col.substring(1, _col.indexOf("_")));
    } else {
      periodsSet.add(_col);
    }
  }

  const _include = ["values"];
  if (getIsDenomYoySelected(state.ratios.include)) {
    _include.push("yoy");
  }

  let _companyIds = [];
  if (optimizedTickerSet.length > 0) {
    _companyIds = optimizedTickerSet.map(
      (_ticker) => _ticker.cid || _ticker.companyid,
    );
  } else {
    _companyIds = state.ticker_info.map(
      (_ticker) => _ticker.cid || _ticker.companyid,
    );
  }
  const _periodType = getPeriodType(state.period);

  let _periods = getSelectedPeriods(state.period, false);
  if (_periodType !== ABS_PERIOD_TYPE.dataId) {
    _periods = _periods.map((item) => convertRelativePeriodLabelToId(item));
  }

  const _otherPeriods = getOtherPeriods(state.period);

  const maxParallelCalls = 40;
  let pointer = 0;
  const companyIdChunks = [];

  for (let i = 0; i < _companyIds.length; i++) {
    if (pointer >= maxParallelCalls) {
      pointer = 0;
    }
    let arr = companyIdChunks[pointer];
    if (arr === undefined) {
      arr = [];
      arr.push(_companyIds[i]);
      companyIdChunks.push(arr);
    } else {
      arr.push(_companyIds[i]);
      companyIdChunks[pointer] = arr;
    }
    pointer++;
  }

  for (let i = 0; i < companyIdChunks.length; i++) {
    const tickerParams = extractTickers(companyIdChunks[i]);
    let dataUrl = `usliid=${[...periodsSet].join(
      "&usliid=",
    )}&viewby=${_include.join("&viewby=")}${tickerParams}`;
    if (_periods && _periods.length) {
      dataUrl += `&period=${_periods.join(
        "&period=",
      )}&period_type=${_periodType}`;
    }
    if (_otherPeriods && _otherPeriods.length) {
      dataUrl += `&other_period=${_otherPeriods.join("&other_period=")}`;
    }

    if (state.period_calculation) {
      if (state.period_calculation.ntm_stm_method) {
        dataUrl += `&ntm_stm_method=${state.period_calculation.ntm_stm_method}`;
      }
      if (state.period_calculation.calendarization_method) {
        dataUrl += `&calendarization_method=${state.period_calculation.calendarization_method}`;
      }
    }

    if (state.currency !== "Base") {
      dataUrl += `&currency=${state.currency}`;
    }

    dataUrl += `&source=vaactuals.consensus`;

    urls.push(dataUrl.trim());
  }
  return urls;
}

export function createComparativeDataCallUrlV1(
  ratios: Array<string>,
  state: VsPeerState,
  optimizedTickerSet: Array<any> = [],
  maxParallelCalls = 10,
  isComparativeAnalysisCall = false,
): Array<string> {
  const urls: Array<string> = [];

  const periodsSet = new Set<string>();
  for (let i = 0; i < ratios.length; i++) {
    const _col = ratios[i];
    if (_col.charAt(0) === "#") {
      periodsSet.add(_col.substring(1, _col.indexOf("_")));
    } else {
      periodsSet.add(_col);
    }
  }

  const _include = ["values"];
  if (getIsDenomYoySelected(state.ratios.include)) {
    _include.push("yoy");
  }

  let _companyIds = [];
  if (optimizedTickerSet.length > 0) {
    _companyIds = optimizedTickerSet.map((_ticker) => _ticker.ticker);
  } else {
    _companyIds = state.ticker_info.map((_ticker) => _ticker.ticker);
  }
  const _periodType = getPeriodType(state.period);

  let _periods = getSelectedPeriods(state.period, false);
  if (_periodType !== ABS_PERIOD_TYPE.dataId) {
    _periods = _periods.map((item) => convertRelativePeriodLabelToId(item));
  }

  const _otherPeriods = getOtherPeriods(state.period);

  let pointer = 0;
  const companyIdChunks = [];

  for (let i = 0; i < _companyIds.length; i++) {
    if (pointer >= maxParallelCalls) {
      pointer = 0;
    }
    let arr = companyIdChunks[pointer];
    if (arr === undefined) {
      arr = [];
      arr.push(_companyIds[i]);
      companyIdChunks.push(arr);
    } else {
      arr.push(_companyIds[i]);
      companyIdChunks[pointer] = arr;
    }
    pointer++;
  }

  for (let i = 0; i < companyIdChunks.length; i++) {
    const tickerParams = extractTickers(
      companyIdChunks[i],
      isComparativeAnalysisCall,
    );
    let dataUrl = `uniquesliid=${[...periodsSet].join(
      "&uniquesliid=",
    )}&viewBy=${_include.join("&viewBy=")}${tickerParams}`;
    if (_periods && _periods.length) {
      dataUrl += `&period=${_periods.join(
        "&period=",
      )}&period_type=${_periodType}`;
    }
    if (_otherPeriods && _otherPeriods.length) {
      dataUrl += `&other_period=${_otherPeriods.join("&other_period=")}`;
    }

    if (state.period_calculation) {
      if (state.period_calculation.ntm_stm_method) {
        dataUrl += `&ntm_stm_method=${state.period_calculation.ntm_stm_method}`;
      }
      if (state.period_calculation.calendarization_method) {
        dataUrl += `&calendarization_method=${state.period_calculation.calendarization_method}`;
      }
    }

    if (state.currency !== "Base") {
      dataUrl += `&currency=${state.currency}`;
    }

    dataUrl += `&source=vaactuals.consensus`;

    urls.push(dataUrl.trim());
  }
  return urls;
}

export function createDataCallUrlV1(
  ratios: Array<string>,
  state: VsPeerState,
  optimizedTickerSet: Array<any> = [],
  maxParallelCalls = 10,
  isComparativeAnalysisCall = false,
): Array<string> {
  const urls: Array<string> = [];

  const periodsSet = new Set<string>();
  for (let i = 0; i < ratios.length; i++) {
    const _col = ratios[i];
    if (_col.charAt(0) === "#") {
      periodsSet.add(_col.substring(1, _col.indexOf("_")));
    } else {
      periodsSet.add(_col);
    }
  }

  const _include = ["values"];
  if (getIsDenomYoySelected(state.ratios.include)) {
    _include.push("yoy");
  }

  let _companyIds = [];
  if (optimizedTickerSet.length > 0) {
    _companyIds = optimizedTickerSet.map(
      (_ticker) => _ticker.cid || _ticker.companyid,
    );
  } else {
    _companyIds = state.ticker_info.map(
      (_ticker) => _ticker.cid || _ticker.companyid,
    );
  }
  const _periodType = getPeriodType(state.period);

  let _periods = getSelectedPeriods(state.period, false);
  if (_periodType !== ABS_PERIOD_TYPE.dataId) {
    _periods = _periods.map((item) => convertRelativePeriodLabelToId(item));
  }

  const _otherPeriods = getOtherPeriods(state.period);

  let pointer = 0;
  const companyIdChunks = [];

  for (let i = 0; i < _companyIds.length; i++) {
    if (pointer >= maxParallelCalls) {
      pointer = 0;
    }
    let arr = companyIdChunks[pointer];
    if (arr === undefined) {
      arr = [];
      arr.push(_companyIds[i]);
      companyIdChunks.push(arr);
    } else {
      arr.push(_companyIds[i]);
      companyIdChunks[pointer] = arr;
    }
    pointer++;
  }

  for (let i = 0; i < companyIdChunks.length; i++) {
    const tickerParams = extractTickers(
      companyIdChunks[i],
      isComparativeAnalysisCall,
    );
    let dataUrl = `usliid=${[...periodsSet].join(
      "&usliid=",
    )}&viewby=${_include.join("&viewby=")}${tickerParams}`;
    if (_periods && _periods.length) {
      dataUrl += `&period=${_periods.join(
        "&period=",
      )}&period_type=${_periodType}`;
    }
    if (_otherPeriods && _otherPeriods.length) {
      dataUrl += `&other_period=${_otherPeriods.join("&other_period=")}`;
    }

    if (state.period_calculation) {
      if (state.period_calculation.ntm_stm_method) {
        dataUrl += `&ntm_stm_method=${state.period_calculation.ntm_stm_method}`;
      }
      if (state.period_calculation.calendarization_method) {
        dataUrl += `&calendarization_method=${state.period_calculation.calendarization_method}`;
      }
    }

    if (state.currency !== "Base") {
      dataUrl += `&currency=${state.currency}`;
    }

    dataUrl += `&source=vaactuals.consensus`;

    urls.push(dataUrl.trim());
  }
  return urls;
}

export function formatCell(data, currency, unit, valueType: string) {
  const sliCompsData = data;
  if (sliCompsData === undefined || sliCompsData === null) {
    return "-";
  }

  if (sliCompsData === "NM") {
    return "NM";
  }

  const valueToUse = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Math.abs(sliCompsData));
  let unitType = unit;
  if (
    !currency ||
    currency === "Base" ||
    valueType === "yoy" ||
    valueType === "revision"
  ) {
    currency = "";
  }
  if (unitType === "%") {
    unitType = valueType === "yoy" || valueType === "revision" ? "bps" : "%";
  } else {
    unitType = valueType === "yoy" || valueType === "revision" ? "%" : "";
  }
  if (sliCompsData < 0) {
    return `(${currency}${valueToUse}${unitType})`;
  }
  return `${currency}${valueToUse}${unitType}`;
}

/**
 * It checks if given period is within range or not
 * @param {number} year: from_to object from period state
 * @param {boolean} isAbsolutePeriod: type object from period state
 * @returns {boolean} checks if given period is within range and returns true/false
 */
export function isPeriodWithinRange(year, isAbsolutePeriod) {
  if (isAbsolutePeriod) {
    return year >= ABS_MIN_YEAR_RANGE && year <= ABS_MAX_YEAR_RANGE;
  }
  return year >= REL_MIN_YEAR_RANGE && year <= REL_MAX_YEAR_RANGE;
}

export function computeValuationGridMedianForMcapAndEv(
  gridData: ValuationGridData,
  currency: string,
) {
  let _medians = {};

  const extraColumns = {
    ev: {
      formatted: "-",
      raw: undefined,
    },
    marketCap: {
      formatted: "-",
      raw: undefined,
    },
  };
  const extraColumnsKeys = Object.keys(extraColumns);
  if (currency === "Base") {
    _medians = { ...extraColumns };
  } else {
    for (let l = 0; l < extraColumnsKeys.length; l++) {
      let currencySymbol = "";
      const item = extraColumnsKeys[l];
      const _vals = [];
      for (let k = 0; k < gridData.rowData.length; k++) {
        const _row = gridData.rowData[k];
        if (_row[item]["raw"] !== undefined) {
          _vals.push(Number(_row[item]["raw"]));
          if (_row[item]["currencySymbol"]) {
            currencySymbol = _row[item]["currencySymbol"];
          }
        }
      }
      if (_vals.length === 0) {
        _medians[item] = {
          formatted: "-",
          raw: undefined,
        };
      } else {
        const sortedValues = _vals.sort((a, b) => a - b);
        const lowMiddle = Math.floor((sortedValues.length - 1) / 2);
        const highMiddle = Math.ceil((sortedValues.length - 1) / 2);
        const median = (sortedValues[lowMiddle] + sortedValues[highMiddle]) / 2;
        _medians[item] = {
          formatted: formatMCapAndEVCell(median, currencySymbol),
          raw: median,
        };
      }
    }
  }
  return _medians;
}

export function computeValuationGridAverageForMcapAndEv(
  gridData: ValuationGridData,
  currency: string,
) {
  let _averages = {};
  const extraColumns = {
    ev: {
      formatted: "-",
      raw: undefined,
    },
    marketCap: {
      formatted: "-",
      raw: undefined,
    },
  };
  const extraColumnsKeys = Object.keys(extraColumns);
  if (currency === "Base") {
    _averages = { ...extraColumns };
  } else {
    for (let l = 0; l < extraColumnsKeys.length; l++) {
      let currencySymbol = "";
      const item = extraColumnsKeys[l];
      let count = 0;
      let sum = 0;
      for (let k = 0; k < gridData.rowData.length; k++) {
        const _row = gridData.rowData[k];
        if (_row[item]["raw"] !== undefined) {
          count++;
          sum = sum + Number(_row[item]["raw"]);
          if (_row[item]["currencySymbol"]) {
            currencySymbol = _row[item]["currencySymbol"];
          }
        }
      }
      if (count === 0) {
        _averages[item] = {
          formatted: "-",
          raw: undefined,
        };
      } else {
        const avg = sum / count;
        _averages[item] = {
          formatted: formatMCapAndEVCell(avg, currencySymbol),
          raw: avg,
        };
      }
    }
  }
  return _averages;
}

export function computeValuationGridAverage(
  gridData: ValuationGridData,
  currency: string,
  calculateFooterDataForMCapAndEV: boolean = true,
): Dictionary<any> {
  let _averages = {};

  // average calculation for extra columns
  if (calculateFooterDataForMCapAndEV) {
    const extraColumns = {
      ev: {
        formatted: "-",
        raw: undefined,
      },
      marketCap: {
        formatted: "-",
        raw: undefined,
      },
    };
    const extraColumnsKeys = Object.keys(extraColumns);
    if (currency === "Base") {
      _averages = { ...extraColumns };
    } else {
      for (let l = 0; l < extraColumnsKeys.length; l++) {
        let currencySymbol = "";
        const item = extraColumnsKeys[l];
        let count = 0;
        let sum = 0;
        for (let k = 0; k < gridData.rowData.length; k++) {
          const _row = gridData.rowData[k];
          if (_row[item]["raw"] !== undefined) {
            count++;
            sum = sum + Number(_row[item]["raw"]);
            if (_row[item]["currencySymbol"]) {
              currencySymbol = _row[item]["currencySymbol"];
            }
          }
        }
        if (count === 0) {
          _averages[item] = {
            formatted: "-",
            raw: undefined,
          };
        } else {
          const avg = sum / count;
          _averages[item] = {
            formatted: formatMCapAndEVCell(avg, currencySymbol),
            raw: avg,
          };
        }
      }
    }
  }

  // average calculation for period columns
  for (let i = 0; i < gridData.ratios.length; i++) {
    let columnCurrencySymbol = "";
    const _ratio = gridData.ratios[i];
    _averages[_ratio] = {};
    for (let j = 0; j < gridData.periods.length; j++) {
      const _p = gridData.periods[j];
      if (
        currency === "Base" &&
        gridData.ratioData[_ratio]["unit"] === "Amount"
      ) {
        _averages[_ratio][_p] = {
          formatted: "-",
          raw: undefined,
        };
        continue;
      }
      let count = 0;
      let sum = 0;

      for (let k = 0; k < gridData.rowData.length; k++) {
        const _row = gridData.rowData[k];
        if (
          _row[_ratio][_p]["raw"] !== undefined &&
          _row[_ratio][_p]["raw"] !== "NM"
        ) {
          count++;
          sum = sum + _row[_ratio][_p]["raw"];
          if (_row[_ratio][_p]["currencySymbol"]) {
            columnCurrencySymbol = _row[_ratio][_p]["currencySymbol"];
          }
        }
      }
      if (count === 0) {
        _averages[_ratio][_p] = {
          formatted: "-",
          raw: undefined,
        };
      } else {
        const avg = sum / count;
        const valueType = _ratio.includes("denom_yoy") ? "yoy" : "values";
        _averages[_ratio][_p] = {
          formatted: formatCell(
            avg,
            gridData.ratioData[_ratio]["currencySymbol"] ||
              columnCurrencySymbol,
            gridData.ratioData[_ratio]["unit"],
            valueType,
          ),
          raw: avg,
        };
      }
    }
  }
  return _averages;
}

export function computeValuationGridMedian(
  gridData: ValuationGridData,
  currency: string,
  calculateFooterDataForMCapAndEV: boolean = true,
): Dictionary<any> {
  let _medians = {};

  // median calculation for extra columns
  if (calculateFooterDataForMCapAndEV) {
    const extraColumns = {
      ev: {
        formatted: "-",
        raw: undefined,
      },
      marketCap: {
        formatted: "-",
        raw: undefined,
      },
    };
    const extraColumnsKeys = Object.keys(extraColumns);
    if (currency === "Base") {
      _medians = { ...extraColumns };
    } else {
      for (let l = 0; l < extraColumnsKeys.length; l++) {
        let currencySymbol = "";
        const item = extraColumnsKeys[l];
        const _vals = [];
        for (let k = 0; k < gridData.rowData.length; k++) {
          const _row = gridData.rowData[k];
          if (_row[item]["raw"] !== undefined) {
            _vals.push(Number(_row[item]["raw"]));
            if (_row[item]["currencySymbol"]) {
              currencySymbol = _row[item]["currencySymbol"];
            }
          }
        }
        if (_vals.length === 0) {
          _medians[item] = {
            formatted: "-",
            raw: undefined,
          };
        } else {
          const sortedValues = _vals.sort((a, b) => a - b);
          const lowMiddle = Math.floor((sortedValues.length - 1) / 2);
          const highMiddle = Math.ceil((sortedValues.length - 1) / 2);
          const median =
            (sortedValues[lowMiddle] + sortedValues[highMiddle]) / 2;
          _medians[item] = {
            formatted: formatMCapAndEVCell(median, currencySymbol),
            raw: median,
          };
        }
      }
    }
  }

  // median calculation for period columns
  for (let i = 0; i < gridData.ratios.length; i++) {
    let columnCurrencySymbol = "";
    const _ratio = gridData.ratios[i];
    _medians[_ratio] = {};
    for (let j = 0; j < gridData.periods.length; j++) {
      const _p = gridData.periods[j];
      if (
        currency === "Base" &&
        gridData.ratioData[_ratio]["unit"] === "Amount"
      ) {
        _medians[_ratio][_p] = {
          formatted: "-",
          raw: undefined,
        };
        continue;
      }
      const _vals = [];
      for (let k = 0; k < gridData.rowData.length; k++) {
        const _row = gridData.rowData[k];
        if (
          _row[_ratio][_p]["raw"] !== undefined &&
          _row[_ratio][_p]["raw"] !== "NM"
        ) {
          _vals.push(_row[_ratio][_p]["raw"]);
          if (_row[_ratio][_p]["currencySymbol"]) {
            columnCurrencySymbol = _row[_ratio][_p]["currencySymbol"];
          }
        }
      }
      if (_vals.length === 0) {
        _medians[_ratio][_p] = {
          formatted: "-",
          raw: undefined,
        };
      } else {
        const sortedValues = _vals.sort((a, b) => a - b);
        const lowMiddle = Math.floor((sortedValues.length - 1) / 2);
        const highMiddle = Math.ceil((sortedValues.length - 1) / 2);
        const median = (sortedValues[lowMiddle] + sortedValues[highMiddle]) / 2;
        const valueType = _ratio.includes("denom_yoy") ? "yoy" : "values";
        _medians[_ratio][_p] = {
          formatted: formatCell(
            median,
            gridData.ratioData[_ratio]["currencySymbol"] ||
              columnCurrencySymbol,
            gridData.ratioData[_ratio]["unit"],
            valueType,
          ),
          raw: median,
        };
      }
    }
  }
  return _medians;
}

//grid Methods
export function createGridColumns(newState: VsPeerState): Dictionary<string> {
  const _denoms = newState.ratios.include.filter((_d) => {
    return _d.selected === true;
  });
  const frequencyMap = {};
  const _ratioColumnMap = {};
  for (let i = 0; i < newState.ratios.ratio_details.length; i++) {
    const _ratio = newState.ratios.ratio_details[i];
    if (_ratio.ratio.selected === true) {
      _ratioColumnMap[_ratio.ratio.usliid] = {
        label: _ratio.ratio.label_unit_mag,
      };

      for (let d = 0; d < _denoms.length; d++) {
        const _denom = _denoms[d];
        const count = frequencyMap[_ratio[_denom.name].usliid];
        if (count) {
          _ratioColumnMap[
            `#${_ratio[_denom.name].usliid}_${count}_${_denom.name}`
          ] = {
            label: _ratio[_denom.name].label_unit_mag,
          };
          frequencyMap[_ratio[_denom.name].usliid] = count + 1;
        } else {
          _ratioColumnMap[`#${_ratio[_denom.name].usliid}_${_denom.name}`] = {
            label: _ratio[_denom.name].label_unit_mag,
          };
          frequencyMap[_ratio[_denom.name].usliid] = 1;
        }
      }
    }
  }
  return _ratioColumnMap;
}

export function createGridRows(
  newState: VsPeerState,
  disableTickerDeletionOnGrid: boolean = false,
): Array<any> {
  const _periods = getSelectedPeriods(newState.period, true);

  const _gridData = newState.ticker_info.map((_ticker) => {
    const obj = {
      ticker: _ticker.ticker,
      cid: _ticker.cid || _ticker.companyid,
      companyName: _ticker.name || _ticker.full_name,
      lastPrice: {
        raw: undefined,
        formatted: undefined,
        currency: undefined,
        rd: undefined,
      },
      marketCap: {
        raw: undefined,
        formatted: undefined,
        currency: newState.currency,
        rd: undefined,
      },
      ev: {
        raw: undefined,
        formatted: undefined,
        currency: newState.currency,
        rd: undefined,
      },
      tickerDeleteDisabled:
        disableTickerDeletionOnGrid || _ticker.tickerDeleteDisabled,
    };

    const _denoms = newState.ratios.include.filter((_d) => {
      return _d.selected === true;
    });
    const frequencyMap = {};

    for (let i = 0; i < newState.ratios.ratio_details.length; i++) {
      const _ratio = newState.ratios.ratio_details[i];
      if (_ratio.ratio.selected === true) {
        const _ratioObj = {};
        for (let p = 0; p < _periods.length; p++) {
          const period = _periods[p];

          _ratioObj[period] = {
            formatted: undefined,
            raw: undefined,
            currency: undefined,
            rd: undefined,
          };
        }

        obj[_ratio.ratio.usliid] = _ratioObj;

        for (let d = 0; d < _denoms.length; d++) {
          const _denom = _denoms[d];
          const _denomObj = {};
          for (let p = 0; p < _periods.length; p++) {
            const period = _periods[p];

            _denomObj[period] = {
              formatted: undefined,
              raw: undefined,
              currency: undefined,
              rd: undefined,
            };
          }

          const count = frequencyMap[_ratio[_denom.name].usliid];
          if (count) {
            obj[`#${_ratio[_denom.name].usliid}_${count}_${_denom.name}`] =
              _denomObj;
            frequencyMap[_ratio[_denom.name].usliid] = count + 1;
          } else {
            obj[`#${_ratio[_denom.name].usliid}_${_denom.name}`] = _denomObj;
            frequencyMap[_ratio[_denom.name].usliid] = 1;
          }
        }
      }
    }
    return obj;
  });
  return _gridData;
}

function processRatioItem(_denoms, frequencyMap, _ratios, _ratioDetails) {
  for (let d = 0; d < _denoms.length; d++) {
    const _denom = _denoms[d];
    const count = frequencyMap[_ratioDetails[_denom.name].usliid];
    if (count) {
      _ratios.push(
        `#${_ratioDetails[_denom.name].usliid}_${count}_${_denom.name}`,
      );
      frequencyMap[_ratioDetails[_denom.name].usliid] = count + 1;
    } else {
      _ratios.push(`#${_ratioDetails[_denom.name].usliid}_${_denom.name}`);
      frequencyMap[_ratioDetails[_denom.name].usliid] = 1;
    }
  }
}

export function createratios(
  newState: VsPeerState,
  orderedRatioItems?,
): Array<string> {
  const _denoms = newState.ratios.include.filter((_d) => {
    return _d.selected === true;
  });
  const frequencyMap = {};
  const _ratios = [];

  if (!orderedRatioItems) {
    for (let i = 0; i < newState.ratios.ratio_details.length; i++) {
      const _ratio = newState.ratios.ratio_details[i];
      if (_ratio.ratio.selected === true) {
        _ratios.push(`${_ratio.ratio.usliid}`);
        processRatioItem(_denoms, frequencyMap, _ratios, _ratio);
      }
    }
  } else {
    for (let j = 0; j < orderedRatioItems.length; j++) {
      _ratios.push(`${orderedRatioItems[j]}`);
      for (let i = 0; i < newState.ratios.ratio_details.length; i++) {
        const _ratio = newState.ratios.ratio_details[i];
        if (_ratio.ratio.usliid === orderedRatioItems[j]) {
          processRatioItem(_denoms, frequencyMap, _ratios, _ratio);
        }
      }
    }
  }

  return _ratios;
}

export function createGridData(
  newState: VsPeerState,
  userSettingDetails?,
  disableTickerDeletionOnGrid?: boolean,
): ValuationGridData {
  const _gridData: ValuationGridData = {};
  _gridData.ratioData = createGridColumns(newState);
  _gridData.rowData = createGridRows(
    newState,
    disableTickerDeletionOnGrid || false,
  );
  const periodList = getSelectedPeriods(newState.period, true);
  _gridData.periods = periodList;
  _gridData.footerData = {
    average: {},
    median: {},
  };
  _gridData.ratios = createratios(newState, userSettingDetails);
  return _gridData;
}

export function createDynamicColumns(newState: VsPeerState): Array<string> {
  const _denoms = newState.ratios.include.filter((_d) => {
    return _d.selected === true;
  });
  const frequencyMap = {};
  const _dynamicColumns = [];
  for (let i = 0; i < newState.ratios.ratio_details.length; i++) {
    const _ratio = newState.ratios.ratio_details[i];
    if (_ratio.ratio.selected === true) {
      _dynamicColumns.push(`${_ratio.ratio.usliid}`);

      for (let d = 0; d < _denoms.length; d++) {
        const _denom = _denoms[d];
        const count = frequencyMap[_ratio[_denom.name].usliid];
        if (count) {
          _dynamicColumns.push(
            `#${_ratio[_denom.name].usliid}_${count}_${_denom.name}`,
          );
          frequencyMap[_ratio[_denom.name].usliid] = count + 1;
        } else {
          _dynamicColumns.push(`#${_ratio[_denom.name].usliid}_${_denom.name}`);
          frequencyMap[_ratio[_denom.name].usliid] = 1;
        }
      }
    }
  }
  return _dynamicColumns;
}

/**
 * Checks if year value exist or not
 * @param {number} value: period value
 * @return {boolean} true/false
 */
export function periodYearValueExist(value) {
  return value !== "" && value !== undefined && typeof value === "number";
}

/**
 * Returns the important log details
 * @param {number|string} primaryCompany: primary ticker company id
 * @param {string} tabType: tab id
 * @return {object} returns main log details
 */
export function getValuationMainExtraLogDetails(primaryCompany, tabType) {
  return {
    ticker: primaryCompany,
    tab_name: tabType,
  };
}

/**
 * Returns the important log details
 * @param {number|string} primaryCompany: primary ticker company id
 * @param {string} tabType: tab id
 * @param {UserSettingObject} userSettingDetails - userSetting object
 * @param {object} periodCalculation - period calculation method details
 * @return {object} returns common log details
 */
export function getExtraLogDetails(
  primaryCompany,
  tabType,
  userSettingDetails,
  periodCalculation,
) {
  const userSettings = userSettingDetails || {};
  return {
    ...getValuationMainExtraLogDetails(primaryCompany, tabType),
    ratios: {
      names: userSettings.selectedRatios,
      denom: userSettings.isDenomSelected,
      denom_yoy: userSettings.isDenomYoySelected,
    },
    period: {
      type: userSettings.periodType,
      basis: userSettings.periodBasis,
      from: userSettings.periodFrom,
      to: userSettings.periodTo,
      other: userSettings.periodOther,
    },
    currency: userSettings.currency,
    peer_ticker: userSettings.tickers,
    orientation: userSettings.orientation,
    period_calculation: periodCalculation,
    source: "VA Actuals/Consensus", //TODO: update this after implementation
  };
}

/**
 * Returns the important log details
 * @param {array} existingCompany: existing array of ticker
 * @param {array} newCompany: updated array of ticker
 * @return {array} array of ticker id difference
 */
export function getUpdatedCompanyIdDifference(existingCompany, newCompany) {
  const tickerIdDifference = [];
  for (const newCompanyItem of newCompany) {
    let exist = false;
    for (const existingCompanyItem of existingCompany) {
      if (newCompanyItem.cid === existingCompanyItem.cid) {
        exist = true;
        break;
      }
    }
    if (!exist) {
      tickerIdDifference.push(newCompanyItem.cid);
    }
  }
  return tickerIdDifference;
}
